import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cachedGet, flush } from "@/lib/offline";
import OfflineBar from "@/components/vi/OfflineBar";
import ScanButton from "@/components/vi/QrScanner";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ProspectDialog } from "@/components/vi/ViDialogs";
import { Card, MobileStock, SalesTable } from "@/components/vi/TourParts";
import { TourActions } from "@/pages/vi/ViTourDetail";
import Recommendations, { RecoHeader } from "@/components/vi/Recommendations";
import RouteCard from "@/components/vi/RouteCard";
import { Bike, UserPlus, Package } from "lucide-react";
import { money } from "@/lib/vi";

function Gauge({ label, value, target, fmt = (v) => v }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : 0;
  return (
    <div className="rounded-xl border bg-white p-3">
      <p className="text-[10px] uppercase font-mono text-slate-400">{label}</p>
      <p className="font-mono font-bold text-sm">{fmt(value)} / {fmt(target || 0)}</p>
      <div className="h-1.5 rounded-full bg-slate-100 mt-1"><div className="h-1.5 rounded-full bg-brand-600" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function ViMyTour() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({});
  const [prospect, setProspect] = useState(false);
  const [showStock, setShowStock] = useState(false);
  const load = async () => { await flush(); cachedGet("/itinerant/my-tour").then(setData).catch(() => setData({ tour: null })); };
  useEffect(() => {
    load(); cachedGet("/itinerant/settings").then(setSettings).catch(() => {});
    cachedGet("/itinerant/clients").catch(() => {}); cachedGet("/articles").catch(() => {}); cachedGet("/prospects").catch(() => {});
    const r = () => cachedGet("/itinerant/my-tour").then(setData).catch(() => {});
    window.addEventListener("vi-synced", r);
    return () => window.removeEventListener("vi-synced", r);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <Loader />;
  const t = data.tour;
  if (!t) return <EmptyState icon={Bike} title="Aucune tournée en cours" subtitle="Votre responsable doit vous affecter une tournée." />;
  return (
    <div className="max-w-xl mx-auto" data-testid="my-tour">
      <OfflineBar />
      <div className="rounded-2xl bg-brand-700 text-white p-4 mb-3">
        <p className="text-[11px] uppercase font-mono opacity-70">Ma tournée · {t.numero}</p>
        <p className="font-head text-xl font-bold">TRICYCLE : {t.tricycle_code}</p>
        <p className="text-sm opacity-90">Circuit : {t.circuit.nom} · {t.date}</p>
        <div className="mt-2"><StatusBadge value={t.statut} /></div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Gauge label="CA" value={t.ca} target={t.objectif_ca} fmt={(v) => Math.round(v).toLocaleString("fr-FR")} />
        <Gauge label="Visites" value={t.visites} target={t.objectif_visites} />
        <Gauge label="Nouveaux clients" value={t.nouveaux} target={t.objectif_nouveaux} />
      </div>
      <TourActions tour={t} reload={load} settings={settings} user={user} />
      <div className="grid grid-cols-3 gap-2 mb-4 [&_button]:!text-xs [&_button]:sm:!text-base [&_button]:!px-1 [&_button]:w-full [&_svg]:!mr-1">
        <ScanButton big />
        <Button variant="outline" className="h-14 text-base font-semibold" onClick={() => setProspect(true)} data-testid="act-prospect"><UserPlus className="h-5 w-5 mr-2" />PROSPECT</Button>
        <Button variant="outline" className="h-14 text-base font-semibold" onClick={() => setShowStock(!showStock)} data-testid="act-stock"><Package className="h-5 w-5 mr-2" />MON STOCK</Button>
      </div>
      {showStock && <Card title="Mon stock"><MobileStock tour={t} /></Card>}
      {["En attente départ", "En tournée"].includes(t.statut) && <Card title={<RecoHeader />} testid="my-reco"><Recommendations tourId={t.id} compact /></Card>}
      {["En attente départ", "En tournée"].includes(t.statut) && <Card title="Itinéraire optimisé du jour" testid="my-route"><RouteCard tourId={t.id} /></Card>}
      {t.anomalies?.length > 0 && <Card title="Écarts à expliquer"><Link to={`/vi/tournees/${t.id}`} className="text-sm text-brand-700 hover:underline">Voir et expliquer les écarts</Link></Card>}
      <Card title={`Mes ventes (${t.sales_count}) · ${money(t.ca)}`}><SalesTable tour={t} canCancel={false} reload={load} /></Card>
      <ProspectDialog open={prospect} onOpenChange={setProspect} />
    </div>
  );
}
